import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router";
import type { CreateProject, ProjectDraftData } from "@workspace/contracts/projects";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { ProjectForm } from "@/features/project/form";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/projects/drafts/$draftId")({
  component: RouteComponent,
  loader: async ({ context: { orpc, queryClient } }) => {
    const [featureOptions, cityList, districtList, distanceOptions] = await Promise.all([
      queryClient.ensureQueryData(orpc.features.optionsAdmin.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.cities.options.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.districts.optionsAdmin.queryOptions()),
      queryClient.ensureQueryData(orpc.distances.options.queryOptions({ input: {} })),
    ]);
    return { featureOptions, cityList, districtList, distanceOptions };
  },
});

function RouteComponent() {
  const { draftId } = Route.useParams();
  const { featureOptions, cityList, districtList, distanceOptions } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = Route.useNavigate();

  const dataQuery = useQuery(orpc.admin.projectDrafts.find.queryOptions({ input: { id: draftId } }));

  const { mutateAsync: updateDraft } = useMutation(orpc.admin.projectDrafts.update.mutationOptions());
  const { mutateAsync: createProject } = useMutation(orpc.admin.projects.create.mutationOptions());

  if (dataQuery.isLoading) return <p>Loading..</p>;

  const draft = dataQuery.data;
  if (draft === undefined) throw notFound();

  const onSaveDraft = async (data: ProjectDraftData) => {
    try {
      const saved = await updateDraft({ id: draft.id, data });
      await queryClient.invalidateQueries({ queryKey: orpc.admin.projectDrafts.list.queryKey() });
      navigate({ to: "/projects/drafts" });
      await router.invalidate({ sync: true });
      await queryClient.invalidateQueries();
      toast.success("Project draft has been saved");
      return saved.data;
    } catch (error) {
      toast.error(getError(error, "Failed to save project draft"));
      return undefined;
    }
  };

  const onSubmit = async (data: CreateProject) => {
    try {
      await createProject({ ...data, draftId: draft.id });
      await queryClient.invalidateQueries({ queryKey: orpc.admin.projects.list.queryKey({ input: {} }) });
      await queryClient.invalidateQueries({
        queryKey: orpc.admin.projectDrafts.find.queryKey({ input: { id: draft.id } }),
      });
      await queryClient.invalidateQueries({ queryKey: orpc.admin.projectDrafts.list.queryKey() });
      toast.success("Project has been created successfully");
      navigate({ to: "/projects" });
    } catch (error) {
      toast.error(getError(error, "Failed to create project"));
    }
  };

  return (
    <ProjectForm
      project={draft.data}
      onSubmit={onSubmit}
      onSaveDraft={onSaveDraft}
      submitLabel="Create project"
      featureOptions={featureOptions}
      cityList={cityList}
      districtList={districtList}
      distanceOptions={distanceOptions}
    />
  );
}
